<template>
  <v-container fluid class="pa-0 px-2">
    <div class="dash-header px-5 py-4">
      <v-row align="center" no-gutters style="gap: 12px">
        <v-col>
          <v-btn-toggle
            v-model="viewType"
            mandatory
            dense
            rounded
            color="primary"
            class="mr-2"
            @change="onViewTypeChange"
          >
            <v-btn value="overall" small class="text-none px-3">
              <v-icon small class="mr-1">mdi-chart-line</v-icon>
              Overall
            </v-btn>
            <v-btn value="daywise" small class="text-none px-3">
              <v-icon small class="mr-1">mdi-calendar-today</v-icon>
              Date Range
            </v-btn>
          </v-btn-toggle>
        </v-col>
        <v-col
          cols="auto"
          class="d-flex flex-wrap align-center"
          style="gap: 10px"
        >
          <!-- Daywise: separate From and To date pickers -->
          <template v-if="viewType === 'daywise'">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedFromDate"
                  readonly
                  outlined
                  dense
                  hide-details
                  label="From"
                  style="min-width: 160px; background: white"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="fromDate"
                :max="toDate || today"
                @input="onFromDateChange"
              />
            </v-menu>

            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedToDate"
                  readonly
                  outlined
                  dense
                  hide-details
                  label="To"
                  style="min-width: 160px; background: white"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="toDate"
                :min="fromDate"
                :max="today"
                @input="onToDateChange"
              />
            </v-menu>
          </template>

          <v-autocomplete
            v-model="form.year_batch"
            :items="batches"
            outlined
            dense
            hide-details
            clearable
            label="Batch"
            prepend-inner-icon="mdi-calendar-range"
            style="min-width: 155px; background: white"
            @change="onBatchChange"
          />

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                small
                color="accent"
                v-bind="attrs"
                v-on="on"
                @click="openApplicationSearchDialog = true"
              >
                <v-icon small>mdi-account-search</v-icon>
              </v-btn>
            </template>
            <span>Application Search</span>
          </v-tooltip>

          <v-progress-circular
            v-if="loading"
            indeterminate
            color="accent"
            width="2"
            size="24"
          />
          <v-tooltip v-else bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                small
                color="accent"
                v-bind="attrs"
                v-on="on"
                @click="initialize"
              >
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Refresh</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>

    <div class="pa-5">
      <v-row class="mb-5">
        <v-col v-for="s in summaryItems" :key="s.label" cols="6" sm="3">
          <v-card flat rounded="lg" class="kpi-card" :style="primaryBorder">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="kpi-card__label">{{ s.label }}</div>
                  <div class="kpi-card__value">{{ s.value }}</div>
                </div>
                <div class="kpi-icon-wrap" :style="`background: ${s.bgColor}`">
                  <v-icon :color="s.color" size="22">{{ s.icon }}</v-icon>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Application Status section -->
      <div class="section-header mb-3">
        <div class="section-accent primary darken-1" />
        <span class="section-title">Application Status</span>
      </div>

      <v-row class="mb-5">
        <v-col
          v-for="status in dashboard.status_group"
          :key="status.value"
          cols="6"
          sm="4"
          md="3"
          lg="2"
          xl="2"
        >
          <v-card
            flat
            rounded="lg"
            class="stat-card"
            :style="primaryBorder"
            @click="
              goToList('Status', status.name, {
                application_status: status.name,
              })
            "
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <v-avatar
                  size="36"
                  :style="`background: ${
                    (statusStyles[status.value] || {}).bg || '#f1f5f9'
                  }`"
                >
                  <v-icon
                    :color="
                      (statusStyles[status.value] || {}).color || '#64748b'
                    "
                    size="18"
                  >
                    {{
                      (statusStyles[status.value] || {}).icon ||
                      "mdi-circle-outline"
                    }}
                  </v-icon>
                </v-avatar>
                <v-icon size="14" color="grey lighten-1"
                  >mdi-arrow-top-right</v-icon
                >
              </div>
              <div class="stat-card__count">{{ status.count }}</div>
              <div class="stat-card__label">{{ status.name }}</div>
              <v-progress-linear
                :value="getBarPct(status.count)"
                :color="(statusStyles[status.value] || {}).color || '#94a3b8'"
                background-color="grey lighten-3"
                height="3"
                rounded
                class="mt-2"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="6" sm="4" md="3" lg="2" xl="2">
          <v-card
            flat
            rounded="lg"
            class="stat-card"
            :style="primaryBorder"
            @click="goToList('Pre Applicant', '', {}, true)"
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <v-avatar size="36" style="background: #ede9fe">
                  <v-icon color="#7c3aed" size="18"
                    >mdi-account-clock-outline</v-icon
                  >
                </v-avatar>
                <v-icon size="14" color="grey lighten-1"
                  >mdi-arrow-top-right</v-icon
                >
              </div>
              <div class="stat-card__count">
                {{ dashboard.pre_application_count }}
              </div>
              <div class="stat-card__label">Pre-Applicants</div>
              <v-progress-linear
                :value="getBarPct(dashboard.pre_application_count)"
                color="#7c3aed"
                background-color="grey lighten-3"
                height="3"
                rounded
                class="mt-2"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Reviewer Verification section -->
      <div class="section-header mb-3">
        <div class="section-accent primary darken-1" />
        <span class="section-title">Verification Status</span>
      </div>

      <v-row class="mb-5">
        <v-col
          v-for="reviewer in dashboard.reviewer_group"
          :key="reviewer.value"
          cols="6"
          sm="4"
          md="3"
          lg="2"
          xl="2"
        >
          <v-card
            flat
            rounded="lg"
            class="stat-card"
            :style="primaryBorder"
            @click="
              goToList('Reviewer', reviewer.name, {
                reviewer_level: reviewer.value === 'verified' ? 1 : 0,
              })
            "
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <v-avatar
                  size="36"
                  :style="`background: ${
                    reviewer.value === 'verified' ? '#d1fae5' : '#fee2e2'
                  }`"
                >
                  <v-icon
                    :color="
                      reviewer.value === 'verified' ? '#059669' : '#dc2626'
                    "
                    size="18"
                  >
                    {{
                      reviewer.value === "verified"
                        ? "mdi-check-decagram-outline"
                        : "mdi-alert-circle-outline"
                    }}
                  </v-icon>
                </v-avatar>
                <v-icon size="14" color="grey lighten-1"
                  >mdi-arrow-top-right</v-icon
                >
              </div>
              <div class="stat-card__count">{{ reviewer.count }}</div>
              <div class="stat-card__label">{{ reviewer.name }}</div>
              <v-progress-linear
                :value="getBarPct(reviewer.count)"
                :color="reviewer.value === 'verified' ? '#059669' : '#dc2626'"
                background-color="grey lighten-3"
                height="3"
                rounded
                class="mt-2"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts row -->
      <v-row class="mb-5">
        <v-col cols="12" md="4">
          <v-card
            flat
            rounded="lg"
            :style="primaryBorder"
            class="chart-card fill-height"
          >
            <div class="chart-card__header px-4 pt-4 pb-2">
              <v-icon small color="primary" class="mr-2"
                >mdi-chart-donut</v-icon
              >
              <span class="chart-card__title">Status Distribution</span>
              <v-spacer />
              <v-tooltip bottom v-if="show_pie_chart()">
                <template #activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="downloadDonut">
                    <v-icon small color="grey darken-1">mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>Download as PNG</span>
              </v-tooltip>
            </div>
            <v-card-text class="pa-3">
              <div
                v-if="show_pie_chart()"
                ref="donutWrap"
                style="position: relative; height: 400px"
              >
                <status-donut-chart
                  :chart-data="statusChartData"
                  :options="doughnutOptions"
                />
              </div>
              <div v-else class="no-data-placeholder" style="height: 300px">
                No data available
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card
            flat
            rounded="lg"
            :style="primaryBorder"
            class="chart-card fill-height"
          >
            <div
              class="chart-card__header px-4 pt-3 pb-2 flex-wrap"
              style="gap: 8px"
            >
              <v-icon small color="indigo darken-1" class="mr-1"
                >mdi-chart-multiple</v-icon
              >
              <span class="chart-card__title">Application Analysis</span>
              <v-btn-toggle
                v-model="activeGroup"
                mandatory
                dense
                rounded
                color="primary"
                class="ml-3"
              >
                <v-btn
                  v-for="g in chartGroups"
                  :key="g.value"
                  :value="g.value"
                  x-small
                  class="text-none px-3"
                >
                  <v-icon x-small class="mr-1">{{ g.icon }}</v-icon
                  >{{ g.label }}
                </v-btn>
              </v-btn-toggle>
              <v-spacer />
              <v-btn-toggle
                v-model="chartType"
                mandatory
                dense
                color="primary"
                class="mr-2"
              >
                <v-tooltip bottom
                  ><template #activator="{ on }"
                    ><v-btn value="bar" small icon v-on="on"
                      ><v-icon small>mdi-chart-bar</v-icon></v-btn
                    ></template
                  ><span>Bar Chart</span></v-tooltip
                >
                <!--
                <v-tooltip bottom
                  ><template #activator="{ on }"
                    ><v-btn value="line" small icon v-on="on"
                      ><v-icon small>mdi-chart-line</v-icon></v-btn
                    ></template
                  ><span>Line Chart</span></v-tooltip
                >-->
                <v-tooltip bottom
                  ><template #activator="{ on }"
                    ><v-btn value="mixed" small icon v-on="on"
                      ><v-icon small>mdi-chart-areaspline</v-icon></v-btn
                    ></template
                  ><span>Mixed (Bar + Line %)</span></v-tooltip
                >
                <v-tooltip bottom
                  ><template #activator="{ on }"
                    ><v-btn value="radar" small icon v-on="on"
                      ><v-icon small>mdi-radar</v-icon></v-btn
                    ></template
                  ><span>Radar Chart</span></v-tooltip
                >
                <v-tooltip bottom
                  ><template #activator="{ on }"
                    ><v-btn value="table" small icon v-on="on"
                      ><v-icon small>mdi-table</v-icon></v-btn
                    ></template
                  ><span>Table View</span></v-tooltip
                >
              </v-btn-toggle>
              <v-tooltip
                bottom
                v-if="chartType === 'table' && tableRows.length"
              >
                <template #activator="{ on }"
                  ><v-btn icon small v-on="on" @click="downloadTableAsCSV"
                    ><v-icon small color="green darken-1"
                      >mdi-download</v-icon
                    ></v-btn
                  ></template
                >
                <span>Download Table as CSV</span>
              </v-tooltip>
              <v-tooltip
                bottom
                v-else-if="
                  (dashboard.program_group.length ||
                    dashboard.partner_group.length) &&
                  chartType !== 'table'
                "
              >
                <template #activator="{ on }"
                  ><v-btn icon small v-on="on" @click="downloadCombinedChart"
                    ><v-icon small color="grey darken-1"
                      >mdi-download</v-icon
                    ></v-btn
                  ></template
                >
                <span>Download as PNG</span>
              </v-tooltip>
            </div>
            <v-divider />
            <v-card-text class="pa-3">
              <div
                v-if="
                  dashboard.program_group.length ||
                  dashboard.partner_group.length
                "
              >
                <div
                  v-if="chartType !== 'table'"
                  ref="combinedChartWrap"
                  style="position: relative; height: 300px"
                >
                  <combined-bar-chart
                    v-if="chartType === 'bar' || chartType === 'mixed'"
                    :chart-data="combinedChartData"
                    :options="combinedBarOptions"
                  />
                  <combined-line-chart
                    v-else-if="chartType === 'line'"
                    :chart-data="combinedChartData"
                    :options="combinedLineOptions"
                  />
                  <combined-radar-chart
                    v-else-if="chartType === 'radar'"
                    :chart-data="combinedChartData"
                    :options="combinedRadarOptions"
                  />
                </div>
                <div v-else>
                  <v-data-table
                    :headers="tableHeaders"
                    :items="tableRows"
                    dense
                    hide-default-footer
                    :items-per-page="-1"
                    item-key="id"
                    show-expand
                    :expanded.sync="expanded"
                    class="combined-table"
                  >
                    <template #[`item.name`]="{ item }"
                      ><div
                        :class="
                          item.isSubPartner ? 'sub-partner-table-row' : ''
                        "
                      >
                        {{ item.name }}
                      </div></template
                    >
                    <template #[`item.color`]="{ item }"
                      ><span
                        class="table-color-dot"
                        :style="`background: ${item.color}`"
                    /></template>
                    <template #[`item.revenue`]="{ item }"
                      ><span>{{
                        item.revenue ? "₹" + formatRevenue(item.revenue) : "—"
                      }}</span></template
                    >
                    <template #[`item.revenue_pct`]="{ item }"
                      ><v-chip
                        v-if="item.revenue_pct"
                        x-small
                        color="green darken-1"
                        outlined
                        class="font-weight-bold px-2"
                        >{{ item.revenue_pct }}%</v-chip
                      ><span v-else>—</span></template
                    >
                    <template
                      v-slot:item.data-table-expand="{
                        item,
                        isExpanded,
                        expand,
                      }"
                    >
                      <v-icon
                        v-if="item.expandable"
                        @click="expand(!isExpanded)"
                        :color="isExpanded ? 'teal' : 'grey'"
                        >{{
                          isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
                        }}</v-icon
                      >
                    </template>
                    <template v-slot:expanded-item="{ headers, item }">
                      <td :colspan="headers.length" class="pa-0">
                        <div
                          v-if="item.sub_partners && item.sub_partners.length"
                          class="px-6"
                        >
                          <div
                            v-for="sp in item.sub_partners"
                            :key="sp.value"
                            class="d-flex align-center justify-space-between"
                          >
                            <span class="sub-partner-name">{{ sp.name }}</span>
                            <div>{{ sp.count }}</div>
                          </div>
                        </div>
                      </td>
                    </template>
                  </v-data-table>
                </div>
              </div>
              <div v-else class="no-data-placeholder" style="height: 300px">
                No data available
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Programmes & Partners -->
      <v-row>
        <v-col cols="12" md="6">
          <div class="section-header mb-3">
            <div class="section-accent primary" />
            <span class="section-title">Programmes</span>
            <v-chip
              x-small
              color="blue lighten-4"
              class="ml-2 font-weight-bold"
              >{{ dashboard.program_group.length }}</v-chip
            >
            <v-spacer />
            <div class="d-flex align-center pr-5">
              <span
                class="pct-label"
                style="min-width: 50px; text-align: center"
                >App %</span
              >
              <span
                class="pct-label"
                style="min-width: 50px; text-align: center"
                >Rev %</span
              >
            </div>
          </div>
          <v-card flat rounded="lg" :style="primaryBorder">
            <v-list dense class="pa-0">
              <v-list-item
                v-for="(program, i) in dashboard.program_group"
                :key="program.value"
                class="list-row"
                @click="
                  goToList('Programme', program.name, {
                    program_id: program.value,
                  })
                "
              >
                <v-list-item-avatar size="28" class="list-row__rank my-0 mr-3"
                  ><span class="list-row__rank-text">{{
                    i + 1
                  }}</span></v-list-item-avatar
                >
                <v-avatar size="32" color="blue lighten-5" class="mr-3"
                  ><v-icon color="primary" size="16"
                    >mdi-school-outline</v-icon
                  ></v-avatar
                >
                <v-list-item-content>
                  <v-list-item-title class="list-row__name">{{
                    program.name
                  }}</v-list-item-title>
                  <v-list-item-subtitle
                    ><v-progress-linear
                      :value="getPct(program.count)"
                      color="primary"
                      background-color="blue lighten-5"
                      height="3"
                      rounded
                      class="mt-1"
                      style="max-width: 110px"
                  /></v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action
                  class="d-flex flex-row align-center my-0"
                  style="gap: 8px"
                >
                  <span class="list-row__count">{{ program.count }}</span>
                  <div class="d-flex flex-column align-center pct-col">
                    <v-chip
                      x-small
                      color="primary"
                      outlined
                      class="font-weight-bold px-2"
                    >
                      {{ getPct(program.count) }}%
                    </v-chip>
                  </div>
                  <div class="d-flex flex-column align-center pct-col">
                    <v-chip
                      x-small
                      color="green darken-1"
                      outlined
                      class="font-weight-bold px-2"
                    >
                      {{
                        program.revenue_pct ? program.revenue_pct + "%" : "—"
                      }}
                    </v-chip>
                  </div>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!dashboard.program_group.length"
                ><v-list-item-content class="text-center grey--text"
                  >No data</v-list-item-content
                ></v-list-item
              >
            </v-list>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <div class="section-header mb-3">
            <div class="section-accent teal darken-1" />
            <span class="section-title">Partners</span>
            <v-chip
              x-small
              color="teal lighten-4"
              class="ml-2 font-weight-bold"
              >{{ dashboard.partner_group.length }}</v-chip
            >
            <v-spacer />
            <div class="d-flex align-center pr-5">
              <span
                class="pct-label"
                style="min-width: 50px; text-align: center"
                >App %</span
              >
              <span
                class="pct-label"
                style="min-width: 50px; text-align: center"
                >Rev %</span
              >
            </div>
          </div>
          <v-card flat rounded="lg" :style="primaryBorder">
            <v-list dense class="pa-0">
              <div
                v-for="(partner, i) in dashboard.partner_group"
                :key="partner.value"
              >
                <v-list-item
                  class="list-row"
                  @click="
                    goToList('Partner', partner.name, {
                      partner_id: partner.value,
                    })
                  "
                >
                  <v-list-item-avatar size="28" class="list-row__rank my-0 mr-3"
                    ><span class="list-row__rank-text">{{
                      i + 1
                    }}</span></v-list-item-avatar
                  >
                  <v-avatar size="32" color="teal lighten-5" class="mr-3"
                    ><v-icon color="teal darken-1" size="16"
                      >mdi-handshake-outline</v-icon
                    ></v-avatar
                  >
                  <v-list-item-content>
                    <v-list-item-title class="list-row__name">
                      {{ partner.name }}
                      <span
                        v-if="
                          partner.sub_partners && partner.sub_partners.length
                        "
                        class="total-count-badge"
                        >Total: {{ partner.count }}</span
                      >
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <div class="d-flex align-center">
                        <v-progress-linear
                          :value="getPct(partner.count)"
                          color="teal darken-1"
                          background-color="teal lighten-5"
                          height="3"
                          rounded
                          class="mt-1"
                          style="max-width: 110px"
                        />
                        <span
                          class="ml-2 breakdown-text"
                          v-if="
                            partner.sub_partners && partner.sub_partners.length
                          "
                        >
                          ({{ partner.name }}:
                          {{ getParentOnlyCount(partner) }} | Sub:
                          {{ getSubTotalCount(partner) }})
                        </span>
                      </div>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action
                    class="d-flex flex-row align-center my-0"
                    style="gap: 8px"
                  >
                    <v-chip
                      v-if="partner.sub_partners && partner.sub_partners.length"
                      small
                      color="teal lighten-5"
                      class="ml-1"
                      style="font-size: 10px; cursor: pointer"
                      @click.stop="togglePartner(partner.value)"
                    >
                      <v-icon left small>{{
                        expandedPartners.includes(partner.value)
                          ? "mdi-chevron-down"
                          : "mdi-chevron-right"
                      }}</v-icon>
                      <span>view</span>
                    </v-chip>
                    <span class="list-row__count">{{
                      getParentOnlyCount(partner)
                    }}</span>
                    <div class="d-flex flex-column align-center pct-col">
                      <v-chip
                        x-small
                        color="teal darken-1"
                        outlined
                        class="font-weight-bold px-2"
                      >
                        {{ getPct(partner.count) }}%
                      </v-chip>
                    </div>
                    <div class="d-flex flex-column align-center pct-col">
                      <v-chip
                        x-small
                        color="green darken-1"
                        outlined
                        class="font-weight-bold px-2"
                      >
                        {{
                          partner.revenue_pct ? partner.revenue_pct + "%" : "—"
                        }}
                      </v-chip>
                    </div>
                  </v-list-item-action>
                </v-list-item>
                <template
                  v-if="
                    partner.sub_partners &&
                    partner.sub_partners.length &&
                    expandedPartners.includes(partner.value)
                  "
                >
                  <v-list-item
                    v-for="sp in partner.sub_partners"
                    :key="`sub-${sp.value}`"
                    class="list-row sub-partner-row"
                    @click="
                      goToList('Partner', sp.name, { partner_id: sp.value })
                    "
                  >
                    <v-avatar
                      size="26"
                      color="teal lighten-5"
                      class="mr-3 ml-11"
                      ><v-icon color="teal lighten-1" size="13"
                        >mdi-subdirectory-arrow-right</v-icon
                      ></v-avatar
                    >
                    <v-list-item-content>
                      <v-list-item-title>{{ sp.name }}</v-list-item-title>
                      <v-list-item-subtitle
                        ><v-progress-linear
                          :value="getPct(sp.count)"
                          color="teal lighten-2"
                          background-color="teal lighten-5"
                          height="3"
                          rounded
                          class="mt-1"
                          style="max-width: 90px"
                      /></v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action
                      class="d-flex flex-row align-center my-0"
                      style="gap: 8px"
                    >
                      <span class="sub-partner-count">{{ sp.count }}</span>
                      <div class="d-flex flex-column align-center pct-col">
                        <v-chip
                          x-small
                          color="teal lighten-2"
                          outlined
                          class="font-weight-bold px-2"
                        >
                          {{ getPct(sp.count) }}%
                        </v-chip>
                      </div>
                      <div class="d-flex flex-column align-center pct-col">
                        <v-chip
                          x-small
                          color="green lighten-1"
                          outlined
                          class="font-weight-bold px-2"
                        >
                          {{ sp.revenue_pct ? sp.revenue_pct + "%" : "—" }}
                        </v-chip>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </div>
              <v-list-item v-if="!dashboard.partner_group.length"
                ><v-list-item-content class="text-center grey--text"
                  >No data</v-list-item-content
                ></v-list-item
              >
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Application Search Dialog -->
    <v-dialog v-model="openApplicationSearchDialog" width="1000" persistent>
      <v-card>
        <v-form ref="searchForm" v-model="valid">
          <v-card-title class="primary white--text">
            Application Search
            <v-spacer />
            <v-btn small icon @click="closeAppSearch"
              ><v-icon small color="white">mdi-close</v-icon></v-btn
            >
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row>
              <v-col cols="12" md="3"
                ><v-text-field
                  v-model="applicationSearch.application_id"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.applicationNumber]"
                  maxlength="15"
                  label="Application ID"
              /></v-col>
              <v-col cols="12" md="3"
                ><v-text-field
                  v-model="applicationSearch.name"
                  outlined
                  dense
                  hide-details="auto"
                  label="Name"
              /></v-col>
              <v-col cols="12" md="3"
                ><v-text-field
                  v-model="applicationSearch.mobile_no"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.mobile]"
                  maxlength="10"
                  label="Mobile No"
              /></v-col>
              <v-col cols="12" md="3" class="d-flex align-center"
                ><v-btn
                  color="primary"
                  depressed
                  :disabled="!canSearch"
                  @click="searchApplication"
                  >Search</v-btn
                ></v-col
              >
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-sheet outlined rounded>
                  <v-data-table
                    :headers="applicationHeaders"
                    :items="applicationReportData"
                    fixed-header
                  >
                    <template #no-data
                      ><div class="text-center py-4 grey--text">
                        No data available
                      </div></template
                    >
                    <template #[`item.reviewed_user_id`]="{ item }">{{
                      getUserName(item.reviewed_user_id || "-")
                    }}</template>
                    <template #[`item.action`]="{ item }"
                      ><v-icon
                        small
                        color="primary"
                        @click="goToEditFromSearch(item)"
                        >mdi-pencil</v-icon
                      ></template
                    >
                  </v-data-table>
                </v-sheet>
              </v-col>
            </v-row>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { Doughnut, Bar, Line, Radar, Pie } from "vue-chartjs";
import { rules } from "@/helpers/rules";
import { applicationHeaders } from "@/constants/headers";

const LIST_STATE_KEY = "report_list_state";
const EDIT_STATE_KEY = "report_edit_state";
const SEARCH_STATE_KEY = "report_search_state";

function readSession(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeSession(key, value) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

const StatusDonutChart = {
  extends: Doughnut,
  props: { chartData: Object, options: Object },
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      handler(v) {
        if (this._chart) this._chart.destroy();
        this.renderChart(v, this.options);
      },
      deep: true,
    },
  },
};
const CombinedBarChart = {
  extends: Bar,
  props: { chartData: Object, options: Object },
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      handler(v) {
        if (this._chart) this._chart.destroy();
        this.renderChart(v, this.options);
      },
      deep: true,
    },
    options: {
      handler(v) {
        if (this._chart) this._chart.destroy();
        this.renderChart(this.chartData, v);
      },
      deep: true,
    },
  },
};
const CombinedLineChart = {
  extends: Line,
  props: { chartData: Object, options: Object },
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      handler(v) {
        if (this._chart) this._chart.destroy();
        this.renderChart(v, this.options);
      },
      deep: true,
    },
  },
};
const CombinedRadarChart = {
  extends: Radar,
  props: { chartData: Object, options: Object },
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      handler(v) {
        if (this._chart) this._chart.destroy();
        this.renderChart(v, this.options);
      },
      deep: true,
    },
  },
};

export default {
  name: "ReportDashboardView",
  components: {
    StatusDonutChart,
    CombinedBarChart,
    CombinedLineChart,
    CombinedRadarChart,
  },

  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      today,
      expanded: [],
      viewType: "overall",
      fromDateMenu: false,
      toDateMenu: false,
      // Both default to today
      fromDate: today,
      toDate: today,
      loading: false,
      valid: false,
      openApplicationSearchDialog: false,
      activeGroup: "status",
      chartType: "bar",
      tableHeaders: [
        { text: "", value: "color", sortable: false, width: "32px" },
        { text: "Name", value: "name", sortable: true },
        { text: "Count", value: "count", sortable: true, align: "right" },
        { text: "Count %", value: "share", sortable: true, align: "right" },
        {
          text: "Revenue (₹)",
          value: "revenue",
          sortable: true,
          align: "right",
        },
        {
          text: "Revenue %",
          value: "revenue_pct",
          sortable: true,
          align: "right",
        },
      ],
      chartGroups: [
        { value: "status", label: "Status", icon: "mdi-list-status" },
        { value: "programme", label: "Programme", icon: "mdi-school-outline" },
        { value: "partner", label: "Partner", icon: "mdi-handshake-outline" },
      ],
      form: { year_batch: null },
      batches: [],
      users: [],
      dashboard: {
        status_group: [],
        program_group: [],
        partner_group: [],
        reviewer_group: [],
        pre_application_count: 0,
      },
      applicationSearch: { application_id: null, name: null, mobile_no: null },
      applicationReportData: [],
      expandedPartners: [],
      applicationHeaders,
      rules,
      // Which date field each status uses in daywise mode — shown as a hint on the card
      daywiseDateField: {
        draft: "by created date",
        submitted: "by submitted date",
        provisionally_approved: "by provisional date",
        approved: "by approved date",
        admitted: "by admitted date",
      },
      statusStyles: {
        draft: {
          icon: "mdi-file-edit-outline",
          color: "#78a6ef",
          bg: "#f1f5f9",
        },
        pending: { icon: "mdi-clock-outline", color: "#d97706", bg: "#fef3c7" },
        approved: {
          icon: "mdi-check-circle-outline",
          color: "#059669",
          bg: "#d1fae5",
        },
        rejected: {
          icon: "mdi-close-circle-outline",
          color: "#dc2626",
          bg: "#fee2e2",
        },
        submitted: { icon: "mdi-check-bold", color: "#2563eb", bg: "#dbeafe" },
        re_submitted: {
          icon: "mdi-reply-circle",
          color: "#7c3aed",
          bg: "#ede9fe",
        },
        provisionally_approved: {
          icon: "mdi-alert-circle-outline",
          color: "#ea580c",
          bg: "#ffedd5",
        },
        admitted: {
          icon: "mdi-account-check-outline",
          color: "#0d9488",
          bg: "#ccfbf1",
        },
      },
      doughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 60,
        legend: {
          position: "right",
          labels: {
            fontSize: 10,
            padding: 10,
            usePointStyle: true,
            boxWidth: 8,
          },
        },
        tooltips: {
          callbacks: {
            label(item, data) {
              return ` ${data.labels[item.index]}: ${
                data.datasets[0].data[item.index]
              }`;
            },
          },
        },
      },
      combinedRadarOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scale: {
          ticks: {
            beginAtZero: true,
            fontSize: 9,
            maxTicksLimit: 5,
            fontColor: "#94a3b8",
            backdropColor: "transparent",
          },
          pointLabels: { fontSize: 10, fontColor: "#475569" },
          gridLines: { color: "#e2e8f0" },
          angleLines: { color: "#e2e8f0" },
        },
      },
    };
  },

  computed: {
    formattedFromDate() {
      if (!this.fromDate) return "";
      return new Date(this.fromDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    formattedToDate() {
      if (!this.toDate) return "";
      return new Date(this.toDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    primaryBorder() {
      return `border: 1px solid ${this.$vuetify.theme.currentTheme.primary}`;
    },
    totalApplications() {
      return this.dashboard.status_group.reduce((s, x) => s + x.count, 0);
    },
    summaryItems() {
      return [
        {
          label: "Total Applications",
          value: this.totalApplications,
          icon: "mdi-file-document-multiple-outline",
          color: "#2563eb",
          bgColor: "#dbeafe",
        },
        {
          label: "Pre-Applicants",
          value: this.dashboard.pre_application_count,
          icon: "mdi-account-clock-outline",
          color: "#7c3aed",
          bgColor: "#ede9fe",
        },
        {
          label: "Programmes",
          value: this.dashboard.program_group.length,
          icon: "mdi-school-outline",
          color: "#0d9488",
          bgColor: "#ccfbf1",
        },
        {
          label: "Partners",
          value: this.dashboard.partner_group.length,
          icon: "mdi-handshake-outline",
          color: "#059669",
          bgColor: "#d1fae5",
        },
      ];
    },
    statusChartData() {
      const statuses = this.dashboard.status_group;
      return {
        labels: statuses.map((s) => s.name),
        datasets: [
          {
            data: statuses.map((s) => s.count),
            backgroundColor: statuses.map(
              (s) => (this.statusStyles[s.value] || {}).color || "#94a3b8",
            ),
            borderWidth: 2,
            borderColor: "#ffffff",
            hoverBorderWidth: 0,
          },
        ],
      };
    },
    combinedBaseData() {
      const blues = [
        "#1d4ed8",
        "#2563eb",
        "#3b82f6",
        "#60a5fa",
        "#93c5fd",
        "#bfdbfe",
      ];
      const teals = [
        "#0f766e",
        "#0d9488",
        "#14b8a6",
        "#2dd4bf",
        "#5eead4",
        "#99f6e4",
      ];
      if (this.activeGroup === "status") {
        return {
          labels: this.dashboard.status_group.map((s) => s.name),
          counts: this.dashboard.status_group.map((s) => s.count),
          colors: this.dashboard.status_group.map(
            (s) => (this.statusStyles[s.value] || {}).color || "#94a3b8",
          ),
        };
      }
      if (this.activeGroup === "programme") {
        const progs = this.dashboard.program_group.slice(0, 12);
        return {
          labels: progs.map((p) => p.name),
          counts: progs.map((p) => p.count),
          colors: progs.map((_, i) => blues[i % blues.length]),
        };
      }
      if (this.activeGroup === "partner") {
        const partners = this.dashboard.partner_group.slice(0, 12);
        const partnerCounts = partners.map((p) =>
          Math.max(
            (p.count || 0) -
              (p.sub_partners || []).reduce((s, sp) => s + (sp.count || 0), 0),
            0,
          ),
        );
        const subCounts = partners.map((p) =>
          (p.sub_partners || []).reduce((s, sp) => s + (sp.count || 0), 0),
        );
        return {
          labels: partners.map((p) => p.name),
          counts: partnerCounts,
          subCounts,
          colors: partners.map((_, i) => teals[i % teals.length]),
        };
      }
      return { labels: [], counts: [], colors: [] };
    },
    tableRows() {
      if (this.activeGroup === "partner") {
        return this.dashboard.partner_group.map((p) => ({
          id: p.value,
          name: p.name,
          count: p.count,
          share: this.getPct(p.count),
          revenue: p.revenue ? Number(p.revenue) : 0,
          revenue_pct: p.revenue_pct || 0,
          color: "#0d9488",
          sub_partners: p.sub_partners || [],
          expandable: !!(p.sub_partners && p.sub_partners.length),
        }));
      }
      const { labels, counts, colors } = this.combinedBaseData;
      const total = counts.reduce((a, b) => a + b, 0) || 1;
      const group =
        this.activeGroup === "programme"
          ? this.dashboard.program_group
          : this.dashboard.status_group;
      return labels.map((name, i) => {
        const item = group.find((x) => x.name === name);
        return {
          id: i,
          name,
          count: counts[i],
          share: +((counts[i] / total) * 100).toFixed(1),
          revenue: item && item.revenue ? Number(item.revenue) : 0,
          revenue_pct: item && item.revenue_pct ? item.revenue_pct : 0,
          color: colors[i],
          expandable: false,
        };
      });
    },
    combinedChartData() {
      const { labels, counts, colors } = this.combinedBaseData;
      const total = counts.reduce((a, b) => a + b, 0) || 1;

      if (
        this.activeGroup === "partner" &&
        (this.chartType === "bar" || this.chartType === "mixed")
      ) {
        const {
          labels: l,
          counts: c,
          subCounts: sc,
          colors: col,
        } = this.combinedBaseData;
        if (this.chartType === "mixed") {
          const totals = c.map((v, i) => v + sc[i]);
          const grand = totals.reduce((s, v) => s + v, 0) || 1;
          return {
            labels: l,
            datasets: [
              {
                type: "bar",
                label: "Partner",
                data: c,
                backgroundColor: col,
                stack: "partners",
                borderWidth: 0,
                yAxisID: "count",
              },
              {
                type: "bar",
                label: "Sub Partner",
                data: sc,
                backgroundColor: "#5eead4",
                stack: "partners",
                borderWidth: 0,
                yAxisID: "count",
              },
              {
                type: "line",
                label: "Percentage",
                data: totals.map((t) => +((t / grand) * 100).toFixed(1)),
                borderColor: "#f59e0b",
                backgroundColor: "transparent",
                pointBackgroundColor: "#f59e0b",
                pointBorderColor: "#fff",
                pointBorderWidth: 2,
                pointRadius: 4,
                fill: false,
                lineTension: 0.3,
                yAxisID: "percent",
              },
            ],
          };
        }
        return {
          labels: l,
          datasets: [
            {
              label: "Partner",
              data: c,
              backgroundColor: col,
              stack: "partners",
              borderWidth: 0,
            },
            {
              label: "Sub Partner",
              data: sc,
              backgroundColor: "#5eead4",
              stack: "partners",
              borderWidth: 0,
            },
          ],
        };
      }
      if (this.activeGroup === "partner" && this.chartType === "line") {
        const {
          labels: l,
          counts: c,
          subCounts: sc,
          colors: col,
        } = this.combinedBaseData;
        return {
          labels: l,
          datasets: [
            {
              label: "Total",
              data: c.map((v, i) => v + sc[i]),
              borderColor: col[0] || "#2563eb",
              backgroundColor: (col[0] || "#2563eb") + "18",
              pointBackgroundColor: col,
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 5,
              fill: true,
              lineTension: 0.35,
            },
          ],
        };
      }
      if (this.activeGroup === "partner" && this.chartType === "radar") {
        const {
          labels: l,
          counts: c,
          subCounts: sc,
          colors: col,
        } = this.combinedBaseData;
        return {
          labels: l,
          datasets: [
            {
              label: "Total",
              data: c.map((v, i) => v + sc[i]),
              backgroundColor: "rgba(37,99,235,0.12)",
              borderColor: "#2563eb",
              pointBackgroundColor: col,
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 4,
              borderWidth: 2,
              lineTension: 0.3,
            },
          ],
        };
      }
      if (this.chartType === "line")
        return {
          labels,
          datasets: [
            {
              label: "Count",
              data: counts,
              borderColor: colors[0] || "#2563eb",
              backgroundColor: (colors[0] || "#2563eb") + "18",
              pointBackgroundColor: colors,
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 5,
              fill: true,
              lineTension: 0.35,
            },
          ],
        };
      if (this.chartType === "mixed") {
        return {
          labels,
          datasets: [
            {
              type: "bar",
              label: "Count",
              data: counts,
              backgroundColor: colors,
              borderWidth: 0,
              yAxisID: "count",
            },
            {
              type: "line",
              label: "Percentage",
              data: counts.map((c) => +((c / total) * 100).toFixed(1)),
              borderColor: "#f59e0b",
              backgroundColor: "transparent",
              pointBackgroundColor: "#f59e0b",
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 4,
              fill: false,
              lineTension: 0.3,
              yAxisID: "percent",
            },
          ],
        };
      }
      if (this.chartType === "radar")
        return {
          labels,
          datasets: [
            {
              label: "Applications",
              data: counts,
              backgroundColor: "rgba(37,99,235,0.12)",
              borderColor: "#2563eb",
              pointBackgroundColor: colors,
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 4,
              borderWidth: 2,
              lineTension: 0.3,
            },
          ],
        };
      return {
        labels,
        datasets: [
          {
            label: "Count",
            data: counts,
            backgroundColor: colors,
            borderWidth: 0,
          },
        ],
      };
    },
    combinedBarOptions() {
      const isMixed = this.chartType === "mixed";
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: isMixed,
          labels: { fontSize: 11, usePointStyle: true },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: { display: false },
              ticks: { fontSize: 10, maxTicksLimit: 14, maxRotation: 30 },
            },
          ],
          yAxes: isMixed
            ? [
                {
                  id: "count",
                  position: "left",
                  gridLines: { color: "#f1f5f9", drawBorder: false },
                  ticks: { beginAtZero: true, precision: 0, fontSize: 10 },
                  scaleLabel: {
                    display: true,
                    labelString: "Count",
                    fontSize: 10,
                  },
                },
                {
                  id: "percent",
                  position: "right",
                  gridLines: { display: false },
                  ticks: {
                    beginAtZero: true,
                    max: 100,
                    fontSize: 10,
                    callback: (v) => v + "%",
                  },
                  scaleLabel: {
                    display: true,
                    labelString: "Percentage",
                    fontSize: 10,
                  },
                },
              ]
            : [
                {
                  stacked: true,
                  gridLines: { color: "#f1f5f9", drawBorder: false },
                  ticks: { beginAtZero: true, precision: 0, fontSize: 10 },
                },
              ],
        },
        tooltips: {
          callbacks: {
            label(item, data) {
              const ds = data.datasets[item.datasetIndex];
              return ` ${ds.label}: ${item.yLabel}${
                ds.label === "Percentage" ? "%" : ""
              }`;
            },
          },
        },
      };
    },
    combinedLineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: { fontSize: 10, maxTicksLimit: 14, maxRotation: 30 },
            },
          ],
          yAxes: [
            {
              gridLines: { color: "#f1f5f9", drawBorder: false },
              ticks: { beginAtZero: true, precision: 0, fontSize: 10 },
            },
          ],
        },
        tooltips: { callbacks: { label: (item) => ` Count: ${item.yLabel}` } },
      };
    },
    canSearch() {
      const { name, application_id, mobile_no } = this.applicationSearch;
      return (!!name || !!application_id || !!mobile_no) && this.valid;
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Operational Dashboard" }]);
    const listState = readSession(LIST_STATE_KEY);
    if (listState?.viewType) {
      this.viewType = listState.viewType;
      if (listState.viewType === "daywise" && listState.fromDate) {
        this.fromDate = listState.fromDate;
        this.toDate = listState.toDate || this.today;
      }
    }
    await this.initialize();
    this.restoreSearchState();
    this.clearListState();
  },

  methods: {
    async initialize() {
      this.loading = true;
      try {
        await this.fetchBatches();
        await Promise.all([this.getCountByStatus(), this.getUsers()]);
      } finally {
        this.loading = false;
      }
    },

    async fetchBatches() {
      try {
        const res = await this.$axios.get("/get_year_batch", {
          params: { portal_admissions: true },
        });
        this.batches = res.data.data;
        const saved = this.getSavedBatch();
        this.form.year_batch =
          saved && this.batches.includes(saved) ? saved : this.batches[0];
      } catch (e) {
        console.error("[fetchBatches]", e);
      }
    },

    async getCountByStatus() {
      this.loading = true;
      try {
        const params = {};
        if (this.form.year_batch) {
          const [year, batch] = this.form.year_batch.split("-");
          params.year = year;
          params.batch = batch;
        }
        if (this.viewType === "daywise") {
          params.date_from = this.fromDate;
          params.date_to = this.toDate;
        }
        const qs = new URLSearchParams(params).toString();
        const res = await this.$axios.get(`/get_report${qs ? "?" + qs : ""}`);
        this.dashboard = res.data.data;
      } catch (e) {
        console.error("[getCountByStatus]", e);
      } finally {
        this.loading = false;
      }
    },

    onViewTypeChange() {
      this.expanded = [];
      this.expandedPartners = [];
      // Reset dates to today when switching to daywise
      if (this.viewType === "daywise") {
        this.fromDate = this.today;
        this.toDate = this.today;
      }
      this.getCountByStatus();
    },

    onFromDateChange() {
      this.fromDateMenu = false;
      // Clamp toDate if it's now before fromDate
      if (this.toDate < this.fromDate) this.toDate = this.fromDate;
      this.getCountByStatus();
    },

    onToDateChange() {
      this.toDateMenu = false;
      this.getCountByStatus();
    },

    async onBatchChange() {
      await this.getCountByStatus();
    },

    async getUsers() {
      try {
        const res = await this.$axios.get("/users");
        this.users = res.data.data;
      } catch (e) {
        console.error("[getUsers]", e);
      }
    },

    async searchApplication() {
      try {
        const qs = new URLSearchParams(
          Object.fromEntries(
            Object.entries(this.applicationSearch).filter(([, v]) => v),
          ),
        ).toString();
        const res = await this.$axios.get(`/applicant_details?${qs}`);
        this.applicationReportData = res.data.data;
      } catch (e) {
        console.error("[searchApplication]", e);
      }
    },

    goToList(headerKey, headerValue, filters = {}, preApplicant = false) {
      writeSession(LIST_STATE_KEY, {
        headerKey,
        headerValue,
        preApplicant,
        filters: { ...filters, year_batch: this.form.year_batch },
        viewType: this.viewType,
        fromDate: this.viewType === "daywise" ? this.fromDate : null,
        toDate: this.viewType === "daywise" ? this.toDate : null,
      });
      this.$router.push({
        name: "Operational Dashboard List",
      });
    },

    goToEditFromSearch(item) {
      writeSession(SEARCH_STATE_KEY, {
        open: true,
        applicationSearch: this.applicationSearch,
        applicationReportData: this.applicationReportData,
        year_batch: this.form.year_batch,
      });
      writeSession(EDIT_STATE_KEY, {
        id: item.id,
        programId: item.program_id,
        applicationId: item.application_id,
        headerKey: "",
        headerValue: "",
        fromDashboard: true,
      });
      this.$router.push({ name: "Operational Dashboard Edit" });
    },

    getSavedBatch() {
      const search = readSession(SEARCH_STATE_KEY);
      if (search?.year_batch) return search.year_batch;
      const list = readSession(LIST_STATE_KEY);
      if (list?.filters?.year_batch) return list.filters.year_batch;
      return null;
    },

    restoreSearchState() {
      const state = readSession(SEARCH_STATE_KEY);
      if (!state?.open) return;
      this.applicationSearch = state.applicationSearch;
      this.applicationReportData = state.applicationReportData;
      this.form.year_batch = state.year_batch;
      this.openApplicationSearchDialog = true;
      sessionStorage.removeItem(SEARCH_STATE_KEY);
    },

    clearListState() {
      const state = readSession(LIST_STATE_KEY);
      if (state?.filters) sessionStorage.removeItem(LIST_STATE_KEY);
    },

    closeAppSearch() {
      this.openApplicationSearchDialog = false;
      this.applicationSearch = {
        application_id: null,
        name: null,
        mobile_no: null,
      };
      this.applicationReportData = [];
      sessionStorage.removeItem(SEARCH_STATE_KEY);
    },

    getUserName(userId) {
      const u = this.users.find((u) => u.id === userId);
      return u ? `${u.first_name} ${u.last_name}` : "";
    },

    getBarPct(count) {
      const max = Math.max(
        ...this.dashboard.status_group.map((s) => s.count),
        this.dashboard.pre_application_count,
        1,
      );
      return Math.round((count / max) * 100);
    },

    getParentOnlyCount(partner) {
      const subTotal = (partner.sub_partners || []).reduce(
        (sum, sp) => sum + (sp.count || 0),
        0,
      );
      return Math.max((partner.count || 0) - subTotal, 0);
    },

    getSubTotalCount(partner) {
      return (partner.sub_partners || []).reduce(
        (sum, sp) => sum + (sp.count || 0),
        0,
      );
    },

    getPct(count) {
      return Math.round((count / (this.totalApplications || 1)) * 100);
    },

    show_pie_chart() {
      return this.dashboard.status_group.some((s) => s.count > 0);
    },

    togglePartner(partnerId) {
      if (this.expandedPartners.includes(partnerId)) {
        this.expandedPartners = this.expandedPartners.filter(
          (id) => id !== partnerId,
        );
      } else {
        this.expandedPartners.push(partnerId);
      }
    },

    formatRevenue(value) {
      if (!value && value !== 0) return "0";
      const num = Number(value);
      if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
      if (num >= 100000) return (num / 100000).toFixed(2) + " L";
      if (num >= 1000) return (num / 1000).toFixed(1) + "K";
      return num.toLocaleString("en-IN");
    },

    downloadChart(canvasEl, filename) {
      const off = document.createElement("canvas");
      off.width = canvasEl.width;
      off.height = canvasEl.height;
      const ctx = off.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, off.width, off.height);
      ctx.drawImage(canvasEl, 0, 0);
      const a = document.createElement("a");
      a.href = off.toDataURL("image/png");
      a.download = filename;
      a.click();
    },

    downloadDonut() {
      const canvas =
        this.$refs.donutWrap && this.$refs.donutWrap.querySelector("canvas");
      if (canvas)
        this.downloadChart(
          canvas,
          `status-distribution-${this.form.year_batch || "all"}.png`,
        );
    },

    downloadCombinedChart() {
      const canvas =
        this.$refs.combinedChartWrap &&
        this.$refs.combinedChartWrap.querySelector("canvas");
      if (canvas)
        this.downloadChart(
          canvas,
          `applications-${this.activeGroup}-${this.chartType}-${
            this.form.year_batch || "all"
          }.png`,
        );
    },

    downloadTableAsCSV() {
      if (!this.tableRows.length) return;
      let csv = "Name,Count,Count %,Revenue (₹),Revenue %\n";
      this.tableRows.forEach((row) => {
        let name = row.name;
        if (name.includes(",") || name.includes('"'))
          name = `"${name.replace(/"/g, '""')}"`;
        csv += `${name},${row.count},${row.share || this.getPct(row.count)},${
          row.revenue || 0
        },${row.revenue_pct || 0}\n`;
        if (
          row.expandable &&
          row.sub_partners &&
          this.expanded.includes(row.id)
        ) {
          row.sub_partners.forEach((sp) => {
            let subName = `  └ ${sp.name}`;
            if (subName.includes(",") || subName.includes('"'))
              subName = `"${subName.replace(/"/g, '""')}"`;
            csv += `${subName},${sp.count},${this.getPct(sp.count)},${
              sp.revenue || 0
            },${sp.revenue_pct || 0}\n`;
          });
        }
      });
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const date = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      const range =
        this.viewType === "daywise"
          ? `-${this.fromDate}-to-${this.toDate}`
          : "";
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `applications-${this.activeGroup}-${
          this.form.year_batch || "all"
        }${range}-${date}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.dash-header {
  background: linear-gradient(135deg, #f0f7ff 0%, #f8fafc 100%);
  border-bottom: 1px solid #e2e8f0;
}
.kpi-card {
  transition: box-shadow 0.18s ease, transform 0.18s ease;
}
.kpi-card:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.09) !important;
  transform: translateY(-2px);
}
.kpi-card__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #64748b;
  margin-bottom: 4px;
}
.kpi-card__value {
  font-size: 32px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -1.5px;
  line-height: 1;
}
.kpi-icon-wrap {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.chart-card {
  transition: box-shadow 0.18s ease;
}
.chart-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.07) !important;
}
.chart-card__header {
  display: flex;
  align-items: center;
}
.chart-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
}
.no-data-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.section-accent {
  width: 4px;
  height: 18px;
  border-radius: 2px;
  flex-shrink: 0;
}
.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}
.date-legend-text {
  font-size: 11px;
  color: #64748b;
}
.stat-card {
  cursor: pointer;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
}
.stat-card:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.09) !important;
  transform: translateY(-3px);
}
.stat-card__count {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -1px;
  line-height: 1;
}
.stat-card__label {
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stat-card__date-field {
  font-size: 10px;
  color: #94a3b8;
  margin-top: 1px;
  font-style: italic;
}
.list-row {
  cursor: pointer;
  transition: background 0.15s ease;
  min-height: 52px !important;
}
.list-row:hover {
  background: #f8fafc !important;
}
.list-row__rank {
  background: #f1f5f9 !important;
  border-radius: 50% !important;
}
.list-row__rank-text {
  font-size: 11px;
  font-weight: 700;
  color: #383c41;
}
.list-row__name {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}
.list-row__count {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.5px;
}
.combined-table >>> thead th {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b !important;
  background: #f8fafc !important;
}
.combined-table >>> tbody tr:hover {
  background: #f8fafc !important;
}
.table-color-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
.sub-partner-table-row {
  padding-left: 18px;
  font-size: 13px;
  color: #0f766e;
}
.sub-partner-name {
  font-size: 13px;
  padding-left: 90px;
}
.total-count-badge {
  font-size: 11px;
  font-weight: 500;
  color: #0d9488;
  background: #ccfbf1;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
}
.breakdown-text {
  font-size: 10px;
  color: #64748b;
}
.pct-col {
  min-width: 50px;
  gap: 2px;
}
.pct-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1e293b;
  line-height: 1;
}
</style>
